import { useEffect, useRef, useState } from "react";
import { useAuthProvider } from "react-admin";
import { useForm } from "react-hook-form";
import ReCAPTCHA from "react-google-recaptcha";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { ThemedPrimaryButton, WhatsappButton } from "../components/ui/button";
import { FormInput } from "../components/ui/form-input";
import { Card } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Subtitle, Title } from "../components/ui/title";
import { registerIphonesImage, tickYellowIcon } from "../assets/images";
import {
  LogoAvatar,
  RIcon,
  PeopleIcon,
  PercentageIcon,
} from "../assets/images/svg";
import { useToast } from "../components/ui/toast";
import {
  EMAIL_FIELD_NAME,
  NAME_FIELD_NAME,
  PHONE_FIELD_NAME,
  errorInEmail,
  validationRules,
  COUNTRY_CODE_FIELD_NAME,
  errorInPhone,
  INSTAGRAM_FIELD_NAME,
} from "./validations";
import { PHONE_REGEX } from "../utils/Validations";
import { CountryDropdownInput } from "../autoOnboarding/components/CountryPicker";
import {
  fireFacebookCompleteRegistration,
  fireFacebookContact,
} from "../services/MetaTracker";
import { validateEmail } from "../utils/AutoOnboarding";
import { setAuthData } from "../utils/AuthUtils";
import ERROR_CODES from "../common/ErrorCodes";
import { useTranslation } from "../contexts/TranslationContext";
import { LightSurface } from "../common/LightSurface";

const UserSchema = z
  .object({
    name: z
      .string()
      .min(1, { message: "El nombre es requerido" })
      .max(100, { message: "El nombre debe tener máximo 100 caracteres" }),
    email: z
      .string()
      .min(1, { message: "El correo electrónico es requerido" })
      .refine((value) => validateEmail(value), {
        message: "El correo electrónico no es válido",
      }),
    phone: z
      .string()
      .min(1, { message: "El teléfono es requerido" })
      .max(18, { message: "El teléfono debe tener máximo 18 caracteres" }),
    countryCode: z
      .string()
      .min(1, { message: "El código de país es requerido" })
      .max(10, {
        message: "El código de país debe tener máximo 10 caracteres",
      }),
    instagram: z
      .string()
      .min(1, "Mínimo 1 caracter")
      .max(30, "Máximo 30 caracteres")
      .trim()
      .optional()
      .or(z.literal("")),
  })
  .refine(
    (data) => {
      if (data.countryCode) {
        const phoneNumber = data.countryCode + data.phone;
        return PHONE_REGEX.test(phoneNumber);
      }
      return false;
    },
    {
      message: ERROR_CODES.PHONE_INVALID_FORMAT,
      path: ["phone"],
    }
  );

const LOGOS_AND_TEXT = [
  {
    image: (className) => (
      <RIcon className={className} style={{ width: "44px" }} />
    ),
    title: "Registro fácil y rápido",
    subtitle:
      "Activa tu local en pocos minutos y comienza a aumentar tus ventas.",
  },
  {
    image: (className) => (
      <PercentageIcon className={className} style={{ width: "44px" }} />
    ),
    title: "Ofrece promociones exclusivas",
    subtitle:
      "Atrae más clientes con ofertas especiales y muestra tus mejores platos.",
  },
  {
    image: (className) => (
      <PeopleIcon className={className} style={{ width: "44px" }} />
    ),
    title: "Recibe clientes todos los días",
    subtitle:
      "Prepárate para dar la bienvenida a nuevos clientes que descubrirán tu local a través de Morfy.",
  },
  {
    image: (className) => (
      <PeopleIcon className={className} style={{ width: "44px" }} />
    ),
    title: "Soporte dedicado",
    subtitle:
      "Nuestro equipo te acompaña en cada paso: desde la carga de tu local hasta la optimización de promociones.",
  },
];

const LogoAndTextComponent = ({ image, title, subtitle }) => {
  return (
    <div className="flex flex-row items-center justify-start gap-3 w-full">
      {image && image("self-start w-8 h-8 text-yellow")}
      <div className="space-y-0 gap-6 w-full">
        <p className="text-left text-base font-semibold mb-0">{title}</p>
        <p className="text-left text-base mb-0">{subtitle}</p>
      </div>
    </div>
  );
};

const { REACT_APP_RECAPTCHA_SITE_KEY } = process.env;
const Register = () => {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [captchaValue, setCaptchaValue] = useState(null);
  const { showToast } = useToast();
  const { register: authRegister } = useAuthProvider();
  const [loading, setLoading] = useState(false);
  const recaptchaRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const role = localStorage.getItem("role");
    if (role) {
      navigate("/");
    }
  }, [navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
    setValue,
    getValues,
  } = useForm({
    resolver: zodResolver(UserSchema),
    defaultValues: {
      [NAME_FIELD_NAME]: "",
      [PHONE_FIELD_NAME]: "",
      [EMAIL_FIELD_NAME]: "",
      [COUNTRY_CODE_FIELD_NAME]: "",
      [INSTAGRAM_FIELD_NAME]: "",
    },
  });

  const resetCaptcha = () => {
    setCaptchaValue(null);
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
    return;
  };

  const { translations } = useTranslation();

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      if (!captchaValue) {
        return showToast("Por favor, complete el captcha.", "info");
      }
      const values = getValues();
      const phoneNumber = values.countryCode + values.phone;
      if (!PHONE_REGEX.test(phoneNumber)) {
        setError("phone", {
          message: "El teléfono debe tener un formato válido.",
        });
      }

      const response = await authRegister({
        email: data.email,
        name: data.name,
        phone: phoneNumber,
        captchaToken: captchaValue,
        ...(data.instagram && { instagram: data.instagram }),
      });

      const errors = response.errors;
      if (errors) {
        const emailError = errorInEmail(errors);
        if (emailError) {
          setError("email", {
            message:
              translations[emailError.msg] || ERROR_CODES.EMAIL_ALREADY_IN_USE,
          });
          return;
        }
        const phoneError = errorInPhone(errors);
        if (phoneError) {
          setError("phone", {
            message:
              translations[phoneError.msg] || ERROR_CODES.PHONE_INVALID_FORMAT,
          });
          return;
        }
        throw new Error();
      }
      const { token } = response;
      setAuthData(token, true);

      fireFacebookCompleteRegistration();

      setShowSuccessModal(true);
      reset();
    } catch (error) {
      // Fallback to the generic error message
      showToast(
        "Hubo un error al enviar el formulario. Por favor, intenta nuevamente.",
        "error"
      );
    } finally {
      resetCaptcha();
      setLoading(false);
    }
  };

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
  };

  const handleCaptchaExpired = () => {
    setCaptchaValue(null);
    showToast(
      "El captcha ha expirado. Por favor, complete el captcha nuevamente.",
      "error"
    );
  };

  return (
    <LightSurface>
      <div
        className="min-h-screen bg-white w-full flex flex-col lg:flex-row overflow-hidden"
        style={{ colorScheme: "light" }}
      >
        {/* Left Section */}
        <div className="w-full lg:w-1/2 bg-white p-4 lg:p-8 flex flex-col items-center justify-center">
          <Card className="w-full max-w-[408px] flex flex-col items-center justify-center border-none py-6">
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="w-full flex flex-col items-center justify-center gap-4 lg:gap-6 px-4"
            >
              <LogoAvatar alt="Morfy" className="w-12 lg:w-16" />
              <div className="space-y-1 lg:space-y-2">
                <Title className="text-center text-xl lg:text-2xl">
                  Haz que tu local se destaque en Morfy
                </Title>
                <Subtitle className="text-center text-sm lg:text-base">
                  Regístrate y llega a cientos de nuevos clientes cada día
                </Subtitle>
              </div>
              <FormInput
                label="Nombre completo"
                name={NAME_FIELD_NAME}
                register={register}
                error={errors.name}
                placeholder="Juan Pérez"
                containerClassName="w-full"
                className="font-medium"
                {...register(NAME_FIELD_NAME, validationRules[NAME_FIELD_NAME])}
              />

              <FormInput
                label="Instagram del local"
                name={INSTAGRAM_FIELD_NAME}
                register={register}
                error={errors.instagram}
                placeholder="@usuario"
                containerClassName="w-full"
                className="font-medium"
                {...register(
                  INSTAGRAM_FIELD_NAME,
                  validationRules[INSTAGRAM_FIELD_NAME]
                )}
              />

              <div className="flex flex-row gap-2 w-full">
                <CountryDropdownInput
                  label="País"
                  name={COUNTRY_CODE_FIELD_NAME}
                  onChange={(value) => setValue("countryCode", value)}
                />
                <FormInput
                  label="Teléfono"
                  name={PHONE_FIELD_NAME}
                  type="tel"
                  register={register}
                  placeholder="11 5566 7788"
                  containerClassName="w-full"
                  className="font-medium"
                  error={errors.phone}
                  {...register(
                    PHONE_FIELD_NAME,
                    validationRules[PHONE_FIELD_NAME]
                  )}
                />
              </div>
              <FormInput
                label="Correo electrónico"
                name={EMAIL_FIELD_NAME}
                register={register}
                error={errors.email}
                placeholder="nombre@tulocal.com"
                containerClassName="w-full"
                className="font-medium"
                {...register(
                  EMAIL_FIELD_NAME,
                  validationRules[EMAIL_FIELD_NAME]
                )}
              />

              <div className="flex justify-center w-full scale-90 lg:scale-100">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={REACT_APP_RECAPTCHA_SITE_KEY}
                  onChange={handleCaptchaChange}
                  onExpired={handleCaptchaExpired}
                />
              </div>

              <ThemedPrimaryButton
                disabled={loading}
                type="submit"
                className="w-full text-base"
              >
                Comenzar registro
              </ThemedPrimaryButton>
              <div className="text-center text-sm flex flex-row gap-2 items-center justify-center">
                <span className="text-neutralStrongBlack font-semibold">
                  ¿Ya tienes una cuenta?
                </span>
                <a href="/#/login" className="text-darkYellow font-medium">
                  Inicia sesión
                </a>
              </div>
            </form>

            <div className="flex flex-col items-center justify-center gap-3 p-6">
              <WhatsappButton
                text="¿Dudas? Chatea con nosotros"
                onPress={() => fireFacebookContact()}
              />
            </div>
          </Card>
        </div>

        {/* Right Section */}
        <div className="w-full lg:w-1/2 p-4 flex flex-col justify-center">
          <div className="bg-yellow rounded-3xl h-full flex flex-col justify-between overflow-hidden grow">
            <div className="w-full flex flex-col justify-between items-center gap-4 lg:gap-10 p-6 lg:p-16">
              <p className="text-neutralStrongBlack text-base lg:text-xl font-semibold text-center w-full">
                Únete a Morfy y accede a una comunidad de más de 8 millones de
                usuarios que buscan dónde comer.
              </p>
              <div className="flex flex-col items-center justify-center gap-4 lg:gap-8 w-full">
                {LOGOS_AND_TEXT.map((logoAndText) => (
                  <LogoAndTextComponent
                    key={logoAndText.title}
                    {...logoAndText}
                  />
                ))}
              </div>
            </div>
            <div className="w-full flex justify-center items-end">
              <img
                src={registerIphonesImage}
                alt="Morfy"
                className="w-3/4 lg:w-2/3 flex grow max-h-[35vh] lg:max-h-[43vh] object-contain"
              />
            </div>
          </div>
        </div>
      </div>

      <Dialog
        open={showSuccessModal}
        // open={true}
        onOpenChange={(open) => {
          if (!open) return; // Prevent closing
          setShowSuccessModal(open);
        }}
      >
        <DialogContent className="w-[320px] sm:w-auto" showCloseButton={false}>
          <DialogHeader className="flex flex-col items-center justify-between gap-4">
            <div className="flex flex-col items-center justify-center gap-2">
              <img src={tickYellowIcon} alt="tick" className="w-16 md:w-24" />
            </div>
            <div className="flex flex-col items-center justify-center gap-6">
              <DialogTitle className="text-xl md:text-3xl font-bold text-center">
                ¡Tu registro fue exitoso!
              </DialogTitle>
              <DialogDescription className="pt-4 space-y-4 text-base md:text-lg font-medium text-center">
                <span className="md:px-10 text-neutralDarkBlack font-medium block">
                  <strong className="font-bold">
                    Revisa tu correo electrónico
                  </strong>{" "}
                  y sigue las instrucciones para activar tu cuenta.
                </span>
                <span className="md:px-10 text-neutralDarkBlack block">
                  Luego podrás iniciar sesión y acceder al panel de
                  administración de tu local.
                </span>
              </DialogDescription>
            </div>
            <ThemedPrimaryButton
              loading={loading}
              onClick={() => {
                setShowSuccessModal(false);
                showToast(
                  "¡Todo listo! Revisa tu correo electrónico para activar tu cuenta.",
                  "success"
                );
                setLoading(true);
                navigate("/#/", { replace: true });
              }}
            >
              Entendido
            </ThemedPrimaryButton>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </LightSurface>
  );
};

export default Register;
